import { CommonModule } from '@angular/common';
import { Component, computed, inject, input, output } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { L10nTranslationModule } from 'angular-l10n';
import { INotification } from '../../event-store/interfaces/event.interface';
import { NotificationService } from '../../notification-store/notification.service';

/** How many unread notifications the bell shows before deferring to the full view. */
export const COMPACT_LIMIT = 5;

/**
 * The compact unread list behind the header bell.
 *
 * Reads the same store slice as the full view, so marking one read here updates the badge and the
 * list at once. "See more" is the only way out of the compact list: it navigates to `/notifications`,
 * which is the same content without the 5-item ceiling.
 */
@Component({
  selector: 'wbi-notification-menu',
  imports: [
    CommonModule,
    L10nTranslationModule,
    MatButtonModule,
    MatIconModule,
    MatDividerModule,
    MatTooltipModule,
  ],
  templateUrl: './notification-menu.component.html',
  styleUrl: './notification-menu.component.scss',
})
export class NotificationMenuComponent {
  private readonly notificationService = inject(NotificationService);
  private readonly router = inject(Router);

  /** Where "see more" goes. Overridable so a host app can mount the view on another path. */
  readonly fullViewRoute = input<string>('/notifications');

  /** Lets the host close the menu overlay — the component has no handle on its own trigger. */
  readonly closed = output<void>();

  readonly unread = this.notificationService.unread;
  readonly unreadCount = this.notificationService.unreadCount;

  readonly compact = computed<INotification[]>(() => this.unread().slice(0, COMPACT_LIMIT));

  /** True when the list is truncated, so "see more" can say how many are hidden. */
  readonly hiddenCount = computed(() => Math.max(0, this.unreadCount() - COMPACT_LIMIT));

  markRead(notification: INotification, event: Event): void {
    // The row is inside a menu: without this, marking one read also dismisses the overlay.
    event.stopPropagation();
    this.notificationService.markRead(notification.id);
  }

  markAllRead(event: Event): void {
    event.stopPropagation();
    this.notificationService.markAllRead();
  }

  seeMore(): void {
    this.closed.emit();
    void this.router.navigate([this.fullViewRoute()]);
  }
}
